import React, { useContext } from "react";
import { Link } from "react-router-dom";
import {
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Card,
  CardContent,
  CardActionArea,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  Typography,
  alpha,
  useTheme,
  Chip,
  Fade,
  Slide,
  Paper,
  Tooltip,
  Skeleton,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import VisibilityIcon from "@mui/icons-material/Visibility";
import StorefrontIcon from "@mui/icons-material/Storefront";
import DateRangeIcon from "@mui/icons-material/DateRange";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import StarIcon from "@mui/icons-material/Star";

import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import {
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

import bgBanner from "../../../assets/db_gif.gif";
import MeetingCard from "../../../widgets/MeetingCard";
import { SimilarExhibitors } from "./SimilarExhibitors";
import { UserContext } from "../../context/UserContext";
import NotificationsWidget from "../common/NotificationsWidget";
import "react-circular-progressbar/dist/styles.css";
import MyChatList from "../common/MyChatList";
import useApiEp from "../../../hooks/useApiEp";

const Dashboard = () => {
  const theme = useTheme();
  const { user } = useContext(UserContext);

  const { data: viewCount, isLoading: isViewCountLoading } =
    useApiEp(`/user/my-views-count`);
  const { data: stallViewCount, isLoading: isStallViewCountLoading } = useApiEp(
    `/user/stall-views-count`
  );
  const { data: meetingCount, isLoading: isMeetingCountLoading } = useApiEp(
    `/user/get-meeting-count`
  );
  const { data: meetings, isLoading: isMeetingsLoading } = useApiEp(
    `/user/get-meetings-short`
  );
  const { data: teamMembers } = useApiEp(`/user/get-team-members`);
  const { data: mutualFavs } = useApiEp(`/user/get-mutual-likes-count`);
  const { data: pc } = useApiEp(`/user/get-exhibitor-profile-completion`);
  const { data: leads } = useApiEp(`/user/get-scanned-users`);

  const leadTypesData = React.useMemo(() => {
    if (!leads || !Array.isArray(leads)) return [];
    const counts = { Hot: 0, Warm: 0, Cold: 0 };
    leads.forEach((lead) => {
      const type = lead.leadType || "Cold";
      if (counts[type] !== undefined) {
        counts[type]++;
      } else {
        counts["Cold"]++;
      }
    });
    return [
      { name: "Hot Leads", value: counts.Hot, color: "#ef4444", icon: "🔥" },
      { name: "Warm Leads", value: counts.Warm, color: "#f59e0b", icon: "🌤️" },
      { name: "Cold Leads", value: counts.Cold, color: "#3b82f6", icon: "❄️" },
    ].filter((d) => d.value > 0);
  }, [leads]);

  const leadsTimelineData = React.useMemo(() => {
    if (!leads || !Array.isArray(leads)) return [];
    const dates = {};
    leads.forEach((lead) => {
      if (lead.createdOn) {
        const dateStr = new Date(lead.createdOn).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
        });
        dates[dateStr] = (dates[dateStr] || 0) + 1;
      }
    });
    return Object.entries(dates).map(([date, count]) => ({ date, count }));
  }, [leads]);

  const userRole = user?.role || "exhibitor";

  const statsConfigs = [
    {
      title: "Stall Views",
      count: !isStallViewCountLoading ? stallViewCount?.count : null,
      color: "#ff981f",
      icon: <StorefrontIcon sx={{ fontSize: 28, color: "#ff981f" }} />,
      link: `/${userRole}/stall-viewers`,
      isLoading: isStallViewCountLoading,
    },
    {
      title: "Profile Views",
      count: !isViewCountLoading ? viewCount?.count : null,
      color: "#fb4d24",
      icon: <VisibilityIcon sx={{ fontSize: 28, color: "#fb4d24" }} />,
      link: `/${userRole}/profile-viewers`,
      isLoading: isViewCountLoading,
    },
    {
      title: "Meeting Requests",
      count: !isMeetingCountLoading ? meetingCount?.count : null,
      color: "#004490",
      icon: <DateRangeIcon sx={{ fontSize: 28, color: "#004490" }} />,
      link: `/${userRole}/meetings`,
      isLoading: isMeetingCountLoading,
    },
    {
      title: "Leads Captured",
      count: leads ? leads.length : null,
      color: "#539506",
      icon: <GroupAddIcon sx={{ fontSize: 28, color: "#539506" }} />,
      link: `/${userRole}/leads`,
      isLoading: !leads && leads !== null,
    },
  ];

  // Compute real conversion rate: meetings out of stall views
  const conversionRate = React.useMemo(() => {
    const sv = stallViewCount?.count || 0;
    const mc = meetingCount?.count || 0;
    if (!sv) return "0.0";
    return ((mc / sv) * 100).toFixed(1);
  }, [stallViewCount, meetingCount]);

  return (
    <Box
      sx={{
        p: { xs: 2, md: 4 },
        backgroundColor: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      {/* Header Section with Stats */}
      <Box sx={{ mb: 5 }}>
        <Grid
          container
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <Grid item xs={12} md={7}>
            <Box>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 0.5 }}
              >
                <Typography
                  variant="h4"
                  fontWeight={800}
                  sx={{ color: "#0f172a", letterSpacing: "-0.02em" }}
                >
                  Dashboard
                </Typography>
                <Chip
                  label="Live"
                  size="small"
                  sx={{
                    bgcolor: "#10b981",
                    color: "white",
                    fontWeight: 600,
                    "& .MuiChip-dot": { bgcolor: "#34d399" },
                    animation: "pulse 2s infinite",
                    "@keyframes pulse": {
                      "0%": { opacity: 1 },
                      "50%": { opacity: 0.7 },
                      "100%": { opacity: 1 },
                    },
                  }}
                  icon={
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: "#34d399",
                        ml: 1,
                      }}
                    />
                  }
                />
              </Box>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ fontWeight: 400 }}
              >
                Real-time insights and analytics for your event presence
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                justifyContent: { xs: "flex-start", md: "flex-end" },
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  px: 3,
                  py: 1.5,
                  borderRadius: 3,
                  bgcolor: "#f1f5f9",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  border: "1px solid #e2e8f0",
                }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 500, display: "block" }}
                  >
                    Stall Views
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{ color: "#0f172a", lineHeight: 1 }}
                  >
                    {isStallViewCountLoading ? (
                      <Skeleton width={40} height={24} />
                    ) : (
                      stallViewCount?.count ?? 0
                    )}
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 500, display: "block" }}
                  >
                    Conversion Rate
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{ color: "#10b981", lineHeight: 1 }}
                  >
                    {isMeetingCountLoading || isStallViewCountLoading ? (
                      <Skeleton width={48} height={24} />
                    ) : (
                      `${conversionRate}%`
                    )}
                  </Typography>
                </Box>
              </Paper>
              {/* <Button 
          variant="outlined" 
          startIcon={<DownloadIcon />}
          sx={{ 
            borderRadius: 3, 
            textTransform: "none",
            borderColor: "#e2e8f0",
            color: "#475569",
            "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" }
          }}
        >
          Export 
        </Button> */}
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Welcome Banner & Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 5 }}>
        {/* Main Welcome Banner */}
        <Grid item xs={12} lg={6}>
          <Slide direction="right" in timeout={500}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 4,
                position: "relative",
                overflow: "hidden",
                height: "100%",
                minHeight: 180,
                cursor: "pointer",
                border: "1px solid rgba(255,255,255,0.1)",
                "&:hover .banner-overlay": {
                  transform: "scale(1.08)",
                },
                "&:hover .banner-gradient": {
                  opacity: 0.8,
                },
              }}
            >
              {/* Background Image */}
              <Box
                className="banner-overlay"
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundImage: `url(${bgBanner})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  transition: "transform 0.7s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              />

              {/* Gradient Overlay */}
              <Box
                className="banner-gradient"
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    "linear-gradient(135deg, rgba(15, 23, 42, 0.92) 0%, rgba(15, 23, 42, 0.4) 60%, rgba(15, 23, 42, 0.1) 100%)",
                  transition: "opacity 0.5s ease",
                }}
              />

              {/* Animated Accent Line */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: 6,
                  height: "100%",
                  background:
                    "linear-gradient(180deg, #6366f1, #8b5cf6, #a855f7)",
                  zIndex: 3,
                }}
              />

              {/* Content */}
              <CardContent
                sx={{
                  position: "relative",
                  zIndex: 3,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  p: { xs: 3, md: 4 },
                  color: "white",
                }}
              >
                {/* <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
            <Chip 
              label="🎉 Welcome Back" 
              size="small" 
              sx={{ 
                bgcolor: "rgba(255,255,255,0.15)", 
                color: "white", 
                width: "fit-content",
                backdropFilter: "blur(12px)",
                fontWeight: 600,
                border: "1px solid rgba(255,255,255,0.1)",
                "& .MuiChip-label": { px: 2 }
              }} 
            />
            <Chip 
              label="🌟 Featured Exhibitor" 
              size="small" 
              sx={{ 
                bgcolor: "rgba(251, 191, 36, 0.2)", 
                color: "#fbbf24", 
                width: "fit-content",
                backdropFilter: "blur(12px)",
                fontWeight: 600,
                border: "1px solid rgba(251, 191, 36, 0.2)",
              }} 
            />
          </Box> */}

                <Typography
                  variant="h3"
                  fontWeight={800}
                  sx={{
                    mb: 1,
                    fontSize: { xs: "1.75rem", sm: "2rem", md: "2rem" },
                    lineHeight: 1.2,
                  }}
                >
                  Welcome, {user?.companyName?.value || "Partner"}!
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    opacity: 0.9,
                    maxWidth: 520,
                    mb: 2,
                    fontSize: { xs: "0.95rem", md: "1.05rem" },
                  }}
                >
                  Your event performance is looking great today. Here are your
                  key metrics at a glance.
                </Typography>

                <Box
                  sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 3 }}
                >
                  {user?.hallNumber?.value && (
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.75,
                        px: 2,
                        py: 0.75,
                        borderRadius: 10,
                        bgcolor: "rgba(88, 125, 255, 0.12)",
                        backdropFilter: "blur(12px)",
                        border: "1px solid rgba(255,255,255,0.2)",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(255,255,255,0.65)",
                          fontWeight: 600,
                          letterSpacing: 0.5,
                          textTransform: "uppercase",
                          fontSize: "0.65rem",
                        }}
                      >
                        Hall:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "white", fontWeight: 700 }}
                      >
                        {user.hallNumber.value}
                      </Typography>
                    </Box>
                  )}
                  {user?.standNumber?.value && (
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.75,
                        px: 2,
                        py: 0.75,
                        borderRadius: 10,
                        bgcolor: "rgba(88, 125, 255, 0.12)",
                        backdropFilter: "blur(12px)",
                        border: "1px solid rgba(255,255,255,0.2)",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(255,255,255,0.65)",
                          fontWeight: 600,
                          letterSpacing: 0.5,
                          textTransform: "uppercase",
                          fontSize: "0.65rem",
                        }}
                      >
                        Stall:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "white", fontWeight: 700 }}
                      >
                        {user.standNumber.value}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>  
            {meetingCount?.count > 0 && (
              <Chip 
                icon={<DateRangeIcon sx={{ fontSize: 16 }} />} 
                label={`${meetingCount.count} Meetings`}
                sx={{ 
                  bgcolor: "rgba(99, 102, 241, 0.15)", 
                  color: "#a5b4fc", 
                  borderColor: "rgba(99, 102, 241, 0.3)",
                  "& .MuiChip-icon": { color: "#a5b4fc" }
                }}
                variant="outlined"
                size="small"
              />
            )}
            {mutualFavs?.count > 0 && (
              <Chip 
                icon={<GroupAddIcon sx={{ fontSize: 16 }} />} 
                label={`${mutualFavs.count} Connections`}
                sx={{ 
                  bgcolor: "rgba(16, 185, 129, 0.15)", 
                  color: "#34d399", 
                  borderColor: "rgba(16, 185, 129, 0.3)",
                  "& .MuiChip-icon": { color: "#34d399" }
                }}
                variant="outlined"
                size="small"
              />
            )}
            {leads?.length > 0 && (
              <Chip 
                icon={<TrendingUpIcon sx={{ fontSize: 16 }} />} 
                label={`${leads.length} Leads`}
                sx={{ 
                  bgcolor: "rgba(251, 191, 36, 0.15)", 
                  color: "#fbbf24", 
                  borderColor: "rgba(251, 191, 36, 0.3)",
                  "& .MuiChip-icon": { color: "#fbbf24" }
                }}
                variant="outlined"
                size="small"
              />
            )}
          </Box> */}
              </CardContent>
            </Card>
          </Slide>
        </Grid>

        {/* Quick Stats Cards */}
        <Grid item xs={12} sm={6} lg={3}>
          <Fade in timeout={600}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 4,
                height: "100%",
                border: "1px solid #eef2f6",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0px 12px 24px -8px rgba(0, 0, 0, 0.08)",
                  borderColor: "#6366f1",
                },
                background: "linear-gradient(135deg, #ffffff 0%, #fafcff 100%)",
              }}
            >
              <CardContent
                sx={{
                  p: 3,
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  minHeight: 180,
                }}
              >
                <Box sx={{ position: "relative", mb: 1.5 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      bgcolor: "rgba(99, 102, 241, 0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 1,
                      mx: "auto",
                    }}
                  >
                    <GroupAddIcon sx={{ fontSize: 28, color: "#6366f1" }} />
                  </Box>
                  <Box
                    sx={{
                      position: "absolute",
                      top: -4,
                      right: -4,
                      bgcolor: "#10b981",
                      color: "white",
                      borderRadius: "50%",
                      width: 20,
                      height: 20,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 10,
                      fontWeight: 700,
                      border: "2px solid white",
                    }}
                  >
                    {teamMembers?.data?.length || 0}
                  </Box>
                </Box>

                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ color: "#0f172a", mb: 0.5 }}
                >
                  Team Members
                </Typography>

                <Typography
                  variant="h2"
                  fontWeight={800}
                  sx={{
                    color: "#6366f1",
                    mb: 2,
                    fontFamily: "monospace",
                    fontSize: "2.5rem",
                  }}
                >
                  {String(
                    teamMembers?.totalItems ?? teamMembers?.data?.length ?? 0
                  ).padStart(2, "0")}
                </Typography>

                {teamMembers?.data && teamMembers.data.length > 0 ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", mb: 2.5 }}
                  >
                    <AvatarGroup
                      max={4}
                      sx={{
                        "& .MuiAvatar-root": {
                          width: 40,
                          height: 40,
                          border: "2px solid white",
                        },
                      }}
                    >
                      {teamMembers.data.slice(0, 4).map((member, idx) => (
                        <Tooltip
                          key={idx}
                          title={`${member.firstName} ${member.lastName}`}
                        >
                          <Avatar
                            src={member.profile_image}
                            alt={member.firstName}
                          />
                        </Tooltip>
                      ))}
                    </AvatarGroup>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 1,
                      mb: 2.5,
                    }}
                  >
                    <Avatar sx={{ width: 40, height: 40, bgcolor: "#e2e8f0" }}>
                      +
                    </Avatar>
                    <Avatar sx={{ width: 40, height: 40, bgcolor: "#e2e8f0" }}>
                      +
                    </Avatar>
                    <Avatar sx={{ width: 40, height: 40, bgcolor: "#e2e8f0" }}>
                      +
                    </Avatar>
                  </Box>
                )}

                <Button
                  component={Link}
                  to="/exhibitor/team-members"
                  variant="contained"
                  size="small"
                  sx={{
                    borderRadius: 3,
                    textTransform: "none",
                    px: 3,
                    bgcolor: "#f1f5f9",
                    color: "#334155",
                    fontWeight: 600,
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: "#e2e8f0",
                      boxShadow: "none",
                    },
                  }}
                  endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                >
                  {teamMembers?.data && teamMembers.data.length > 0
                    ? "Manage Team"
                    : "Add Team"}
                </Button>
              </CardContent>
            </Card>
          </Fade>
        </Grid>

        {/* Profile Completion Card */}
        <Grid item xs={12} sm={6} lg={3}>
          <Fade in timeout={700}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 4,
                height: "100%",
                border: "1px solid #eef2f6",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0px 12px 24px -8px rgba(99, 102, 241, 0.15)",
                  borderColor: "#6366f1",
                },
                background: "linear-gradient(135deg, #ffffff 0%, #fafcff 100%)",
              }}
            >
              <CardContent
                sx={{
                  textAlign: "center",
                  py: 3,
                  px: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  minHeight: 180,
                }}
              >
                <Box
                  sx={{
                    width: 90,
                    height: 90,
                    mx: "auto",
                    mb: 2,
                    position: "relative",
                  }}
                >
                  <CircularProgressbar
                    strokeWidth={10}
                    value={pc?.profileCompletionPercentage ?? 0}
                    text={`${pc?.profileCompletionPercentage ?? 0}%`}
                    styles={buildStyles({
                      pathColor:
                        pc?.profileCompletionPercentage >= 80
                          ? "#10b981"
                          : pc?.profileCompletionPercentage >= 50
                          ? "#f59e0b"
                          : "#6366f1",
                      textColor: "#0f172a",
                      trailColor: "#f1f5f9",
                      textSize: "20px",
                      pathTransitionDuration: 0.5,
                    })}
                  />
                  {pc?.profileCompletionPercentage === 100 && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: -6,
                        right: -6,
                        bgcolor: "#10b981",
                        borderRadius: "50%",
                        width: 24,
                        height: 24,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "2px solid white",
                      }}
                    >
                      <StarIcon sx={{ fontSize: 14, color: "white" }} />
                    </Box>
                  )}
                </Box>

                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{ color: "#0f172a", mb: 0.5 }}
                >
                  My Profile
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2.5, fontSize: "0.8rem", lineHeight: 1.4 }}
                >
                  {pc?.profileCompletionPercentage >= 80
                    ? "🌟 Excellent! Profile is fully optimized"
                    : pc?.profileCompletionPercentage >= 50
                    ? "📈 Good progress! Keep improving"
                    : "⚡ Complete your profile for better visibility"}
                </Typography>

                {pc?.profileCompletionPercentage < 100 && (
                  <Button
                    component={Link}
                    to="/exhibitor/profile"
                    variant="contained"
                    size="small"
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      borderRadius: 3,
                      textTransform: "none",
                      px: 3,
                      bgcolor: "#6366f1",
                      boxShadow: "0px 4px 12px rgba(99, 102, 241, 0.3)",
                      "&:hover": {
                        bgcolor: "#4f46e5",
                        boxShadow: "0px 6px 16px rgba(99, 102, 241, 0.4)",
                      },
                    }}
                  >
                    {pc?.profileCompletionPercentage >= 50
                      ? "Continue"
                      : "Get Started"}
                  </Button>
                )}

                {pc?.profileCompletionPercentage === 100 && (
                  <Button
                    component={Link}
                    to="/exhibitor/profile"
                    variant="outlined"
                    size="small"
                    sx={{
                      borderRadius: 3,
                      textTransform: "none",
                      px: 3,
                      borderColor: "#10b981",
                      color: "#10b981",
                      "&:hover": {
                        borderColor: "#059669",
                        bgcolor: "rgba(16, 185, 129, 0.05)",
                      },
                    }}
                  >
                    View Profile
                  </Button>
                )}
              </CardContent>
            </Card>
          </Fade>
        </Grid>
      </Grid>

      {/* Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statsConfigs.map((stat, idx) => {
          const isLoading = !stat.count && stat.count !== 0;

          return (
            <Grid item xs={12} sm={6} md={3} key={idx}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 4,
                  border: "1px solid",
                  borderColor: theme.palette.divider,
                  backgroundColor: theme.palette.background.paper,
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  position: "relative",
                  overflow: "hidden",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0px 12px 24px rgba(0, 0, 0, 0.04)",
                    borderColor: alpha(
                      stat.color || theme.palette.primary.main,
                      0.3
                    ),
                    "& .stat-icon-box": {
                      transform: "scale(1.08)",
                      backgroundColor: alpha(
                        stat.color || theme.palette.primary.main,
                        0.12
                      ),
                    },
                  },
                }}
              >
                <CardActionArea
                  component={Link}
                  to={stat.link}
                  disabled={isLoading}
                  sx={{ p: 3, height: "100%" }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        color="text.secondary"
                        sx={{
                          textTransform: "uppercase",
                          letterSpacing: 0.8,
                          display: "block",
                          mb: 0.5,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {stat.title}
                      </Typography>

                      {isLoading ? (
                        <Skeleton
                          variant="text"
                          width="60%"
                          height={48}
                          sx={{ borderRadius: 1 }}
                        />
                      ) : (
                        <Typography
                          variant="h4"
                          fontWeight={850}
                          sx={{
                            color: theme.palette.text.primary,
                            letterSpacing: "-0.5px",
                            lineHeight: 1.2,
                          }}
                        >
                          {stat.count}
                        </Typography>
                      )}
                    </Box>

                    <Box
                      className="stat-icon-box"
                      sx={{
                        p: 1.75,
                        borderRadius: 3,
                        backgroundColor: alpha(
                          stat.color || theme.palette.primary.main,
                          0.06
                        ),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        ml: 2,
                        flexShrink: 0,
                      }}
                    >
                      {stat.icon}
                    </Box>
                  </Box>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      {/* Analytics Row: Recent Activity + Lead Distribution */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 4,
              border: "1px solid #eef2f6",
              height: "100%",
            }}
          >
            <Box
              sx={{
                p: 3,
                borderBottom: "1px solid #f1f5f9",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="h6"
                fontWeight={800}
                sx={{ color: "#1e293b" }}
              >
                Notifications
              </Typography>
            </Box>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
                <NotificationsWidget />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 4,
              border: "1px solid #eef2f6",
              height: "100%",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                }}
              >
                <Box>
                  <Typography
                    variant="h6"
                    fontWeight={800}
                    sx={{ color: "#1e293b" }}
                  >
                    Lead Distribution
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Quality breakdown of your connections
                  </Typography>
                </Box>
                <Chip
                  label={`Total: ${leads?.length || 0}`}
                  size="small"
                  variant="outlined"
                />
              </Box>
              <Box sx={{ width: "100%", height: 320 }}>
                {leadTypesData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={leadTypesData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={3}
                        dataKey="value"
                        label={({ name, percent }) =>
                          `${name} ${(percent * 100).toFixed(0)}%`
                        }
                        labelLine={{ stroke: "#94a3b8", strokeWidth: 1 }}
                      >
                        {leadTypesData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.color}
                            stroke="white"
                            strokeWidth={2}
                          />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    <Typography color="text.secondary">
                      No lead data available yet
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recommendations & Interactions Section */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 4,
              border: "1px solid #eef2f6",
              height: "100%",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="h6"
                fontWeight={800}
                sx={{ color: "#1e293b", mb: 2 }}
              >
                AI Recommendations
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mb: 2 }}
              >
                Personalized for your industry
              </Typography>
              <Box sx={{ maxHeight: 420, overflowY: "auto" }}>
                <SimilarExhibitors />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 4,
              border: "1px solid #eef2f6",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardContent sx={{ flex: 1, p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Box>
                  <Typography
                    variant="h6"
                    fontWeight={800}
                    sx={{ color: "#1e293b" }}
                  >
                    Upcoming Meetings
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Scheduled connections
                  </Typography>
                </Box>
                <Button
                  component={Link}
                  to={`/${userRole}/meetings`}
                  size="small"
                  endIcon={<ArrowForwardIcon />}
                >
                  View All
                </Button>
              </Box>
              <Box sx={{ flex: 1, overflowY: "auto", maxHeight: 380 }}>
                {(!meetings ||
                  (Array.isArray(meetings) && meetings.length === 0) ||
                  (meetings.data && meetings.data.length === 0)) &&
                  !isMeetingsLoading && (
                    <Paper
                      sx={{
                        p: 4,
                        textAlign: "center",
                        bgcolor: "#f8fafc",
                        borderRadius: 2,
                      }}
                    >
                      <Typography color="text.secondary">
                        No scheduled meetings
                      </Typography>
                      <Button variant="outlined" size="small" sx={{ mt: 2 }}>
                        Schedule One
                      </Button>
                    </Paper>
                  )}
                {isMeetingsLoading && <LinearProgress sx={{ my: 2 }} />}
                <Stack spacing={2}>
                  {(Array.isArray(meetings) ? meetings : meetings?.data || [])
                    ?.slice(0, 3)
                    .map((item, i) => (
                      <MeetingCard key={i} meetingItem={item} />
                    ))}
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 4,
              border: "1px solid #eef2f6",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardContent sx={{ flex: 1, p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Box>
                  <Typography
                    variant="h6"
                    fontWeight={800}
                    sx={{ color: "#1e293b" }}
                  >
                    Messages
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Unread conversations
                  </Typography>
                </Box>
                <Button
                  component={Link}
                  to={`/${userRole}/chat`}
                  size="small"
                  endIcon={<ArrowForwardIcon />}
                >
                  View All
                </Button>
              </Box>
              <Box sx={{ flex: 1, overflowY: "auto", maxHeight: 380 }}>
                <MyChatList internal={true} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
